import React,{useCallback,useEffect,useState} from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import DisputeEvidencePanel from '../orders/DisputeEvidencePanel';
import {
  fetchDisputeMessages,
  fetchSellerDisputes,
  sendSellerDisputeReply
} from '../../services/deliveryConfirmationService';

const {FiAlertTriangle,FiCheckCircle,FiClock,FiLoader}=FiIcons;

const statusClass={
  open:'bg-red-50 text-red-600',
  investigating:'bg-amber-50 text-amber-700',
  resolved:'bg-emerald-50 text-emerald-700',
  rejected:'bg-slate-100 text-slate-600'
};

const SellerDisputesPanel=({seller})=>{
  const [disputes,setDisputes]=useState([]);
  const [selected,setSelected]=useState(null);
  const [activeTab,setActiveTab]=useState('current');
  const [messages,setMessages]=useState([]);
  const [reply,setReply]=useState('');
  const [refund,setRefund]=useState('');
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [error,setError]=useState('');

  const load=useCallback(async()=>{
    if (!seller?.id) return;

    setLoading(true);
    setError('');

    try {
      const rows=await fetchSellerDisputes(seller.id);
      setDisputes(rows);
      setSelected((current)=>(
        current ? rows.find((item)=>item.id===current.id) || null : null
      ));
    } catch (requestError) {
      setError(requestError.message || 'Unable to load disputes.');
    } finally {
      setLoading(false);
    }
  },[seller?.id]);

  useEffect(()=>{
    load();
  },[load]);

  useEffect(()=>{
    if (!selected?.id) return;

    fetchDisputeMessages(selected.id)
      .then(setMessages)
      .catch((requestError)=>setError(requestError.message || 'Unable to load replies.'));
  },[selected?.id]);

  const closedDisputes=disputes.filter((dispute)=>(
    ['resolved','rejected'].includes(dispute.status)
  ));
  const currentDisputes=disputes.filter((dispute)=>(
    !['resolved','rejected'].includes(dispute.status)
  ));
  const visibleDisputes=activeTab==='closed' ? closedDisputes : currentDisputes;

  const submit=async(resolve=false)=>{
    if (!selected) return;

    if (reply.trim().length<10) {
      setError('Please provide a reply of at least 10 characters.');
      return;
    }

    const refundAmount=refund==='' ? 0 : Number(refund);

    if (!Number.isFinite(refundAmount) || refundAmount<0) {
      setError('Enter a valid refund amount.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await sendSellerDisputeReply({
        disputeId:selected.id,
        response:reply,
        refundAmount,
        resolve
      });
      setReply('');
      setRefund('');
      await load();
      setSelected((current)=>(
        resolve
          ? null
          : disputes.find((item)=>item.id===current?.id) || current
      ));
    } catch (requestError) {
      setError(requestError.message || 'Unable to send the dispute reply.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-3xl bg-slate-50 p-16">
        <SafeIcon icon={FiLoader} className="animate-spin text-2xl text-indigo-500" />
      </div>
    );
  }

  return (
    <section className="rounded-[2.5rem] border border-slate-100 bg-white p-8 shadow-sm">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-black text-slate-900">Order disputes</h3>
          <p className="mt-1 text-sm font-medium text-slate-500">
            Review buyer issues, reply, and issue a full or partial refund.
          </p>
        </div>
        <SafeIcon icon={FiAlertTriangle} className="text-2xl text-amber-500" />
      </div>

      {error && (
        <p className="mb-5 rounded-2xl bg-red-50 p-4 text-sm font-bold text-red-700">
          {error}
        </p>
      )}

      {!disputes.length ? (
        <div className="rounded-2xl bg-slate-50 p-10 text-center text-sm font-bold text-slate-400">
          No disputes have been reported for your orders.
        </div>
      ) : (
        <>
          <div className="mb-5 flex flex-wrap gap-2" role="tablist" aria-label="Dispute status">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab==='current'}
              onClick={()=>{
                setActiveTab('current');
                setSelected(null);
              }}
              className={`rounded-xl px-4 py-3 text-[10px] font-black uppercase tracking-widest ${
                activeTab==='current'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-500'
              }`}
            >
              Current disputes ({currentDisputes.length})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab==='closed'}
              onClick={()=>{
                setActiveTab('closed');
                setSelected(null);
              }}
              className={`rounded-xl px-4 py-3 text-[10px] font-black uppercase tracking-widest ${
                activeTab==='closed'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-500'
              }`}
            >
              Closed disputes ({closedDisputes.length})
            </button>
          </div>

          {!visibleDisputes.length ? (
            <div className="rounded-2xl bg-slate-50 p-10 text-center text-sm font-bold text-slate-400">
              {activeTab==='closed'
                ? 'No closed disputes.'
                : 'No current disputes have been reported for your orders.'}
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
              <div className="space-y-3">
                {visibleDisputes.map((dispute)=>{
                  const isClosed=['resolved','rejected'].includes(dispute.status);

                  return (
                    <button
                      key={dispute.id}
                      type="button"
                      aria-expanded={selected?.id===dispute.id}
                      onClick={()=>setSelected((current)=>(
                        current?.id===dispute.id ? null : dispute
                      ))}
                      className={`w-full rounded-2xl border p-4 text-left ${selected?.id===dispute.id ? 'border-indigo-300 bg-indigo-50' : 'border-slate-100 bg-white'}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs font-black text-indigo-600">
                          #{dispute.order_id?.slice(0,8).toUpperCase()}
                        </span>
                        <span className={`rounded-full px-2 py-1 text-[9px] font-black uppercase ${statusClass[dispute.status] || statusClass.open}`}>
                          {dispute.status}
                        </span>
                      </div>
                      <p className="mt-2 text-sm font-black capitalize text-slate-900">
                        {dispute.reason?.replaceAll('_',' ')}
                      </p>
                      <p className="mt-1 line-clamp-1 text-xs text-slate-500">
                        {isClosed
                          ? dispute.resolution_note || (dispute.status==='rejected' ? 'Dispute rejected.' : 'Dispute resolved.')
                          : dispute.description || 'No description provided.'}
                      </p>
                    </button>
                  );
                })}
              </div>

              {selected && (
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-6">
                  <div className="flex items-center gap-2 text-sm font-black text-slate-900">
                    {selected.status==='resolved'
                      ? <SafeIcon icon={FiCheckCircle} className="text-emerald-600" />
                      : <SafeIcon icon={FiClock} className="text-amber-500" />}
                    {selected.reason?.replaceAll('_',' ')}
                  </div>

                  {['resolved','rejected'].includes(selected.status) && (
                    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Resolution result
                      </p>
                      <p className={`mt-2 text-sm font-black capitalize ${selected.status==='resolved' ? 'text-emerald-700' : 'text-slate-700'}`}>
                        {selected.status==='resolved' ? 'Resolved' : 'Rejected'}
                      </p>
                      <p className="mt-2 text-sm font-bold text-slate-700">
                        {Number(selected.refund_amount || 0)>0
                          ? `Refund issued: $${Number(selected.refund_amount).toFixed(2)}`
                          : 'No refund issued'}
                      </p>
                      {selected.resolution_note && (
                        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">
                          {selected.resolution_note}
                        </p>
                      )}
                      {selected.stripe_refund_id && (
                        <p className="mt-2 break-all text-[10px] text-slate-400">
                          Stripe refund: {selected.stripe_refund_id}
                        </p>
                      )}
                    </div>
                  )}

                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">
                    {selected.description}
                  </p>

                  <DisputeEvidencePanel
                    disputeId={selected.id}
                    orderId={selected.order_id}
                  />

                  <div className="mt-6 space-y-3">
                    {messages.map((message)=>(
                      <div key={message.id} className="rounded-xl bg-white p-4 shadow-sm">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                          {message.sender?.name || message.sender_role} · {new Date(message.created_at).toLocaleString()}
                        </p>
                        <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                          {message.body}
                        </p>
                      </div>
                    ))}
                  </div>

                  {['open','investigating'].includes(selected.status) && (
                    <div className="mt-6 space-y-3">
                      <textarea
                        value={reply}
                        onChange={(event)=>setReply(event.target.value)}
                        rows="4"
                        placeholder="Explain what happened and how you will resolve it..."
                        className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none focus:border-indigo-400"
                      />
                      <div className="flex flex-wrap items-center gap-3">
                        <label className="text-xs font-bold text-slate-500">
                          Refund amount
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={refund}
                            onChange={(event)=>setRefund(event.target.value)}
                            placeholder="0.00"
                            className="ml-2 w-28 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                          />
                        </label>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={()=>submit(false)}
                          className="rounded-xl bg-indigo-600 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-50"
                        >
                          {saving ? 'Sending...' : 'Reply'}
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={()=>submit(true)}
                          className="rounded-xl bg-emerald-600 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-50"
                        >
                          Resolve & refund
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default SellerDisputesPanel;
